import { useEditorStore } from 'CanvasCore/uiSlice'
import { gradient2Toning } from 'CanvasCore/utils'
import type { MutableRefObject } from 'react'
import {
  cropCenterFromSquare,
  getAvatarOutputDimensions,
  getComponentCanvasSize,
  isFullscreenAspect,
  isPortraitAspect,
  normalizeStaticFullscreenType,
} from './fullscreenType'

type CaptureNode = {
  toDataURL?: (opts: object) => string
  toCanvas?: (opts?: object) => HTMLCanvasElement
}

type CaptureOptions = {
  /** 为 true 时允许含 draft 图层（如下载预览）；上传仍应校验 */
  allowDraft?: boolean
}

/** 与 CanvasPanel/Overlay 上传 Avatar 时相同的 drawData 与预览图 dataURL。 */
export function captureCanvasAvatarPayload(
  tid: string | undefined,
  stage: { h: number } | undefined,
  drawRef: MutableRefObject<CaptureNode | null> | null,
  fullscreenType?: number | null,
  options?: CaptureOptions,
): { avatar: string; drawData: Record<string, unknown> } | { error: 'draft' } | null {
  if (!tid) return null
  const node = drawRef?.current
  if (!node || !stage?.h) return null

  const type = normalizeStaticFullscreenType(fullscreenType)
  const fullscreen = isFullscreenAspect(type)
  const portrait = isPortraitAspect(type)
  const base = getComponentCanvasSize(type)
  const output = getAvatarOutputDimensions(type)

  // 全屏：按 912 正方形导出，再只裁左右得到 512×912
  // 3:4：整张画布缩成 512×683，不裁
  // 普通：导出 512×512
  const renderSize = fullscreen ? 912 : output.h
  const pixelRatio = renderSize / stage.h

  let avatar: string
  if (portrait && typeof node.toCanvas === 'function') {
    const full = node.toCanvas({ pixelRatio })
    const out = document.createElement('canvas')
    out.width = output.w
    out.height = output.h
    out.getContext('2d')!.drawImage(full, 0, 0, output.w, output.h)
    avatar = out.toDataURL('image/png')
  } else if (fullscreen && typeof node.toCanvas === 'function') {
    const square = node.toCanvas({ pixelRatio })
    const cropped = cropCenterFromSquare(square, output.w, output.h)
    avatar = cropped.toDataURL('image/png')
  } else if (typeof node.toDataURL === 'function') {
    avatar = node.toDataURL({
      mimeType: 'image/png',
      pixelRatio,
      quality: 1,
    })
  } else {
    return null
  }

  const drawers = useEditorStore.getState().editors[tid]?.drawers
  const tonings = useEditorStore.getState().editors[tid]?.tonings

  const drawData: Record<string, unknown> = {
    canvas: { w: base.w, h: base.h },
    fullscreenType: type,
    drawers,
    tonings: {},
  }

  if (tonings && typeof tonings === 'object') {
    const _tonings = Object.keys(tonings).reduce<Record<string, unknown>>(
      (acc, key) => {
        const gradient = tonings[key]?.default?.color
        if (gradient) {
          acc[key] = {
            ...tonings[key],
            default: {
              ...tonings[key].default,
              toning: gradient2Toning(gradient),
            },
          }
          const def = acc[key] as { default?: { color?: unknown } }
          if (def.default) delete def.default.color
        }
        return acc
      },
      {},
    )

    drawData.tonings = _tonings
  }

  const drawerList = drawData.drawers as { lid?: string }[] | undefined
  if (
    !options?.allowDraft &&
    drawerList?.some((drawer) => drawer.lid === 'draft')
  ) {
    return { error: 'draft' }
  }

  return { avatar, drawData }
}
